import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, ArrowUpRight } from "lucide-react";
import { APPLY_HREF, CONTACT_EMAIL, slug } from "../lib/site";

const IVORY = "#f3ead2";
const SLATE = "#a8aebd";
const GOLD = "#e9d6a2";
const GOLD_DEEP = "#bfa874";

const ROLES = ["VC or fund manager", "Angel investor or family office", "Founder: startup", "Founder: scaleup", "Advisor or speaker"];

export default function RequestInvitationModal({ open, onClose }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState(ROLES[1]);
  const [note, setNote] = useState("");
  const [sent, setSent] = useState(false);
  const [status, setStatus] = useState("idle");

  useEffect(() => { if (open) { setSent(false); setStatus("idle"); } }, [open]);

  // Esc to close, lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  const send = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      const res = await fetch("/api/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ form: "invitation", name, email, fields: { "I am a": role, "A few words": note }, website: e.target.website.value }),
      });
      if (!res.ok) throw new Error();
      setSent(true);
      setStatus("idle");
      setName("");
      setEmail("");
      setNote("");
    } catch {
      setStatus("error");
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-4"
      data-testid="invite-modal"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0"
        style={{
          background: "rgba(0,0,0,0.75)",
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
          animation: "msFadeIn 360ms ease forwards",
        }}
      />

      {/* Card */}
      <div
        className="relative w-full max-w-md max-h-[calc(100vh-2rem)] overflow-y-auto"
        style={{ animation: "msRise 520ms cubic-bezier(.2,.7,.2,1) forwards" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Decorative top gold line */}
        <div
          aria-hidden
          className="h-px"
          style={{
            background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)`,
          }}
        />
        <div
          className="relative px-8 py-10 md:px-10 md:py-12"
          style={{
            background:
              "linear-gradient(180deg, rgba(10,12,28,0.96) 0%, rgba(4,5,16,0.96) 100%)",
            border: "1px solid rgba(233,214,162,0.12)",
            boxShadow:
              "0 40px 100px -30px rgba(0,0,0,0.9), inset 0 1px 0 rgba(255,255,255,0.04)",
          }}
        >
          {/* Close */}
          <button
            type="button"
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full transition hover:bg-white/[0.05]"
            style={{ color: SLATE }}
            data-testid="invite-modal-close"
            aria-label="Close"
          >
            <X size={16} />
          </button>

          {/* Eyebrow */}
          <p
            className="text-[10px] tracking-[0.36em] uppercase text-center"
            style={{ color: GOLD_DEEP }}
          >
            By Invitation
          </p>

          {/* Title */}
          <h2
            className="font-serif text-center tracking-[-0.012em] leading-[1.1] mt-4"
            style={{
              fontFamily: "Fraunces, serif",
              color: IVORY,
              fontSize: "clamp(1.75rem, 3vw, 2.2rem)",
            }}
          >
            Request an invitation.
          </h2>
          <p
            className="text-center mt-4 text-sm leading-relaxed max-w-sm mx-auto"
            style={{ color: SLATE }}
          >
            Maven Skies is a private community of investors, fund managers,
            founders and advisors. Share a few words and we&apos;ll be in touch.
          </p>

          {sent ? (
            <p className="mt-10 text-center leading-[1.6]" style={{ fontFamily: "Fraunces, serif", color: GOLD, fontSize: 20, animation: "msFadeIn 420ms ease forwards" }} data-testid="invite-confirmation">
              Thank you. Your request has reached us, and we&apos;ll be in touch within one week.
            </p>
          ) : (
          <form onSubmit={send} className="mt-8 space-y-4" data-testid="invite-form">
            <div>
              <label
                className="block text-[10px] tracking-[0.28em] uppercase mb-2"
                style={{ color: SLATE }}
              >
                Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                data-testid="invite-name"
                className="w-full bg-transparent border-b py-2 outline-none text-[15px]"
                style={{
                  borderColor: "rgba(233,214,162,0.18)",
                  color: IVORY,
                }}
              />
            </div>
            <div>
              <label
                className="block text-[10px] tracking-[0.28em] uppercase mb-2"
                style={{ color: SLATE }}
              >
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="invite-email"
                className="w-full bg-transparent border-b py-2 outline-none text-[15px]"
                style={{
                  borderColor: "rgba(233,214,162,0.18)",
                  color: IVORY,
                }}
              />
            </div>
            <div>
              <label
                className="block text-[10px] tracking-[0.28em] uppercase mb-2"
                style={{ color: SLATE }}
              >
                I am a
              </label>
              <div className="flex flex-wrap gap-2 mt-1">
                {ROLES.map((r) => (
                  <button
                    type="button"
                    key={r}
                    onClick={() => setRole(r)}
                    data-testid={`invite-role-${slug(r)}`}
                    className="text-[10.5px] tracking-[0.2em] uppercase px-3 py-1.5 rounded-full transition"
                    style={
                      role === r
                        ? {
                            background: GOLD,
                            color: "#1a1408",
                            border: "1px solid rgba(0,0,0,0)",
                          }
                        : {
                            background: "transparent",
                            color: SLATE,
                            border: "1px solid rgba(233,214,162,0.22)",
                          }
                    }
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label
                className="block text-[10px] tracking-[0.28em] uppercase mb-2"
                style={{ color: SLATE }}
              >
                A few words
              </label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                data-testid="invite-note"
                className="w-full bg-transparent border-b py-2 outline-none text-[15px] resize-none"
                style={{
                  borderColor: "rgba(233,214,162,0.18)",
                  color: IVORY,
                }}
              />
            </div>
            <input name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />
            <div className="pt-4 flex justify-center">
              <button
                type="submit"
                disabled={status === "sending"}
                data-testid="invite-submit"
                className="ms-gold-btn relative inline-flex items-center gap-2 px-8 py-3 rounded-full text-[11px] tracking-[0.28em] uppercase font-semibold overflow-hidden transition-transform duration-300 hover:translate-y-[-1px] disabled:opacity-60"
                style={{
                  color: "#1a1408",
                  background:
                    "linear-gradient(135deg, #f7e9b8 0%, #d4b86a 25%, #f0dba0 50%, #b89752 75%, #e9d199 100%)",
                  backgroundSize: "200% 200%",
                  boxShadow:
                    "0 14px 40px -14px rgba(217,181,108,0.55), inset 0 1px 0 rgba(255,255,255,0.55), inset 0 -1px 0 rgba(120,90,40,0.4)",
                }}
              >
                <span className="relative" style={{ zIndex: 2 }}>
                  {status === "sending" ? "Sending…" : "Send Request"}
                </span>
                <ArrowUpRight size={13} className="relative" style={{ zIndex: 2 }} />
              </button>
            </div>
            {status === "error" && (
              <p className="text-center text-[12.5px]" style={{ color: "#e8a3a3" }} data-testid="invite-error">
                Something went wrong. Please try again, or write to{" "}
                <a href={`mailto:${CONTACT_EMAIL}`} className="underline underline-offset-4">{CONTACT_EMAIL}</a>.
              </p>
            )}
            <p className="pt-2 text-center text-[12.5px]" style={{ color: SLATE }}>
              Applying to one of our programs?{" "}
              <Link to={APPLY_HREF} onClick={onClose} className="underline underline-offset-4 transition-colors hover:text-white" style={{ color: GOLD }} data-testid="invite-apply-link">
                Start your application
              </Link>
            </p>
          </form>
          )}

          <p
            className="text-center mt-7 text-[10px] tracking-[0.28em] uppercase"
            style={{ color: SLATE }}
          >
            {CONTACT_EMAIL}
          </p>
        </div>
        {/* Decorative bottom gold line */}
        <div
          aria-hidden
          className="h-px"
          style={{
            background: `linear-gradient(90deg, transparent, ${GOLD_DEEP}, transparent)`,
          }}
        />
      </div>
    </div>
  );
}
